// Call diagnostics "Routing": the queue and phone-menu rows of one call (`flow_entries` of
// GET /api/v1/routing/decisions, migration 016). Pure parsing and copy, kept apart from the admin model: the call detail
// loads this with the main bundle. Rows share one sequence with the routing decisions, so they interleave in order.
//
// A menu key is a menu choice the caller pressed, never a PIN; rows never say who hung up.
import type * as C from '@bcio-voice/contracts';

const KINDS = ['queue_entered', 'queue_rering', 'queue_answered', 'queue_overflow', 'queue_abandoned', 'ivr_prompted', 'ivr_choice', 'ivr_timeout', 'ivr_invalid', 'ivr_default'] as const;
const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'] as const satisfies readonly C.IvrDigit[];
const TARGET_KINDS = ['user', 'ring_group', 'internal_number', 'voicemail', 'missed_call', 'ivr_menu', 'repeat'] as const;

export type FlowEntryKind = (typeof KINDS)[number];
export type FlowDigit = (typeof DIGITS)[number];
export interface FlowEntryTarget { kind: (typeof TARGET_KINDS)[number]; id?: string }
export interface FlowEntry extends Omit<C.RoutingFlowDecisionEntry, 'kind' | 'digit' | 'target'> {
  kind: FlowEntryKind;
  digit: FlowDigit | null;
  target: FlowEntryTarget | null;
}

const ID = /^[A-Za-z0-9_.:-]{1,128}$/;
const CODE = /^[a-z][a-z0-9_]{0,59}$/;
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function oneOf<T extends string>(list: readonly T[], value: unknown): T | null {
  return typeof value === 'string' && (list as readonly string[]).includes(value) ? (value as T) : null;
}
const id = (value: unknown): string | null => (typeof value === 'string' && ID.test(value) ? value : null);
const count = (value: unknown, min: number, max = Number.MAX_SAFE_INTEGER): number | null => (typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max ? value : null);

function readTarget(value: unknown): FlowEntryTarget | null {
  if (!isRecord(value)) return null;
  const kind = oneOf(TARGET_KINDS, value.kind);
  if (!kind) return null;
  const target = id(value.id);
  return target ? { kind, id: target } : { kind };
}

export function readFlowEntry(value: unknown): FlowEntry | null {
  if (!isRecord(value)) return null;
  const kind = oneOf(KINDS, value.kind), entryId = id(value.id), sequence = count(value.sequence, 1);
  const at = typeof value.at === 'string' && Number.isFinite(Date.parse(value.at)) ? value.at : null;
  if (!kind || !entryId || sequence === null || !at) return null;
  const digit = oneOf(DIGITS, value.digit);
  // The server always writes the key on a choice or a wrong key; without it the row can't be described.
  if ((kind === 'ivr_choice' || kind === 'ivr_invalid') && !digit) return null;
  return {
    id: entryId, sequence, at, kind,
    number_id: id(value.number_id), rule_id: id(value.rule_id), rule_version: count(value.rule_version, 1),
    ring_group_id: id(value.ring_group_id), menu_id: id(value.menu_id), menu_version: count(value.menu_version, 1),
    attempt: count(value.attempt, 0, 1000), digit, target: readTarget(value.target), wait_ms: count(value.wait_ms, 0),
    user_ids: Array.isArray(value.user_ids) ? value.user_ids.map(id).filter((x): x is string => x !== null).slice(0, 50) : [],
    reason: typeof value.reason === 'string' && CODE.test(value.reason) ? value.reason : null,
    answered_by_user_id: id(value.answered_by_user_id),
  };
}

/** `flow_entries` of a decisions response; absent (an API before 016) reads as none. */
export function readFlowEntries(value: unknown): { entries: FlowEntry[]; skipped: number } {
  if (!Array.isArray(value)) return { entries: [], skipped: 0 };
  const entries: FlowEntry[] = [];
  let skipped = 0;
  for (const raw of value.slice(0, 500)) {
    const entry = readFlowEntry(raw);
    if (entry) entries.push(entry);
    else skipped += 1;
  }
  return { entries, skipped: skipped + Math.max(0, value.length - 500) };
}

// ---- Copy -------------------------------------------------------------------------------------------------

export interface FlowNames {
  person: (userId: string) => string | null;
  number: (numberId: string) => { label: string; e164: string } | null;
}

const KEY_WORD: Record<FlowDigit, string> = { '0': '0', '1': '1', '2': '2', '3': '3', '4': '4', '5': '5', '6': '6', '7': '7', '8': '8', '9': '9', '*': 'star (*)', '#': 'pound (#)' };

/** "1 min 05 s" style wait, or null without a wait. */
export function waitWords(ms: number | null): string | null {
  if (ms === null) return null;
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s`;
}

export function flowTargetPhrase(target: FlowEntryTarget | null, names: FlowNames): string {
  if (!target) return 'no target';
  switch (target.kind) {
    case 'user':
      return (target.id ? names.person(target.id) : null) ?? 'a teammate';
    case 'ring_group':
      return 'a ring group';
    case 'internal_number': {
      const n = target.id ? names.number(target.id) : null;
      return n ? (n.label ? `${n.label} (${n.e164})` : n.e164) : 'another of our numbers';
    }
    case 'voicemail':
      return 'voicemail';
    case 'missed_call':
      return 'a missed call (nothing rings)';
    case 'ivr_menu':
      return 'another phone menu';
    case 'repeat':
      return 'the same menu again';
  }
}

const ENTER_REASON: Record<string, string> = { no_answer: 'nobody answered the first ring', busy: 'everyone was busy', unavailable: 'nobody could be reached' };
const OVERFLOW_REASON: Record<string, string> = {
  max_wait: 'the longest wait allowed ran out',
  queue_unavailable: 'the caller couldn’t be held in the queue',
  queue_step_limit: 'the queue reached the most ring passes allowed for one call',
};
const ABANDON_REASON: Record<string, string> = {
  caller_abandoned: 'The call ended while the caller was waiting, before anyone answered. “What happened” above shows how it ended.',
  duration_limit: 'The workspace’s time limit for calls ended the wait.',
  call_ended: 'The call ended while the caller was waiting.',
};
const DEFAULT_REASON: Record<string, string> = { retries_exhausted: 'after the last retry', prompt_limit: 'because one call can hear at most 20 menu prompts' };

/** Short label for the row's source column. */
export const FLOW_KIND_LABEL: Record<FlowEntryKind, string> = {
  queue_entered: 'Queue',
  queue_rering: 'Queue',
  queue_answered: 'Queue',
  queue_overflow: 'Queue',
  queue_abandoned: 'Queue',
  ivr_prompted: 'Phone menu',
  ivr_choice: 'Phone menu',
  ivr_timeout: 'Phone menu',
  ivr_invalid: 'Phone menu',
  ivr_default: 'Phone menu',
};

/** One plain sentence per queue or menu row. */
export function flowEntrySentence(e: FlowEntry, names: FlowNames): string {
  const wait = waitWords(e.wait_ms);
  switch (e.kind) {
    case 'queue_entered':
      return `The call entered the ring group’s queue because ${e.reason ? ENTER_REASON[e.reason] ?? 'nobody answered' : 'nobody answered'}. The caller was answered and waited.`;
    case 'queue_rering':
      return `The group rang again${e.attempt !== null ? ` (pass ${e.attempt})` : ''}${wait ? ` after ${wait} in the queue` : ''}.`;
    case 'queue_answered':
      return `${(e.answered_by_user_id ? names.person(e.answered_by_user_id) : null) ?? 'A teammate'} answered${wait ? ` after ${wait} in the queue` : ''}.`;
    case 'queue_overflow':
      return `The queue overflowed because ${e.reason ? OVERFLOW_REASON[e.reason] ?? 'it couldn’t go on' : 'it couldn’t go on'}${wait ? ` (after ${wait})` : ''}. The call went to ${flowTargetPhrase(e.target, names)}.`;
    case 'queue_abandoned':
      return `${e.reason ? ABANDON_REASON[e.reason] ?? 'The wait ended before anyone answered.' : 'The wait ended before anyone answered.'}${wait ? ` Waited ${wait}.` : ''}`;
    case 'ivr_prompted': {
      const which = e.attempt !== null && e.attempt > 1 ? ` (time ${e.attempt})` : '';
      const why = e.reason === 'timeout' ? ' because no key was pressed in time' : e.reason === 'invalid' ? ' after a key that isn’t an option' : '';
      return `The menu prompt played${which}${why}.`;
    }
    case 'ivr_choice':
      return `The caller pressed ${e.digit ? KEY_WORD[e.digit] : 'a key'}: ${flowTargetPhrase(e.target, names)}.`;
    case 'ivr_timeout':
      return `No key was pressed in time${e.attempt !== null ? ` (attempt ${e.attempt})` : ''}.`;
    case 'ivr_invalid':
      return `The caller pressed ${e.digit ? KEY_WORD[e.digit] : 'a key'}, which isn’t an option in this menu${e.attempt !== null ? ` (attempt ${e.attempt})` : ''}.`;
    case 'ivr_default':
      return `The menu’s default ran${e.reason && DEFAULT_REASON[e.reason] ? ` ${DEFAULT_REASON[e.reason]}` : ''}: ${flowTargetPhrase(e.target, names)}.`;
  }
}

/** Final routing reasons the queue and menu flows write (OPUS-IVR-QUEUES.md section 5), in plain words. */
export const FLOW_REASON_SENTENCE: Readonly<Record<string, string>> = Object.freeze({
  ivr_missed_call: 'The phone menu’s choice or default logs a missed call.',
  ivr_voicemail: 'The phone menu sent the call to voicemail.',
  ivr_menu_unavailable: 'The rule sends calls to a phone menu, but the menu or its prompt wasn’t available to the phone system, so the call was logged as missed without being answered.',
  ivr_menu_loop: 'The phone menus led back to a menu this call had already visited, or went too deep, so the call was logged as missed.',
  ivr_unavailable: 'The phone menu couldn’t be played, so the call was logged as missed.',
  queue_overflow_missed_call: 'Nobody answered while the caller waited in the queue, and the queue’s overflow logs a missed call.',
  queue_overflow_voicemail: 'Nobody answered while the caller waited in the queue, so the call went to voicemail.',
});
